import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, FlaskConical, Calendar, Package, Users, Search, X, ClipboardPlus, Check, Paperclip, ChevronLeft, ChevronRight, Info, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { DatePicker } from '../components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parseISO, isWithinInterval, startOfDay, endOfDay } from 'date-fns';
import { cs } from 'date-fns/locale';

// Helper to get row background color based on ratings
const getRowColorClass = (test) => {
  const summary = test.rating_summary;
  if (!summary) return '';
  
  if (summary.C > 0) return 'bg-red-50 border-red-200 hover:border-red-300';
  if (summary.B > 0) return 'bg-amber-50 border-amber-200 hover:border-amber-300';
  if (summary.A > 0) return 'bg-green-50 border-green-200 hover:border-green-300';
  return '';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'dd.MM.yyyy', { locale: cs });
  } catch {
    return dateStr;
  }
};

// Component to load and display team status on demand
const TeamStatusPopover = ({ testId }) => {
  const [loading, setLoading] = useState(false);
  const [teamStatus, setTeamStatus] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const loadTeamStatus = async () => {
    if (teamStatus) return; // Already loaded
    setLoading(true);
    try {
      const response = await api.get(`/sensory/tests/${testId}/team-status`);
      setTeamStatus(response.data);
    } catch (error) {
      console.error('Error loading team status:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Popover open={isOpen} onOpenChange={(open) => {
        setIsOpen(open);
        if (open) loadTeamStatus();
      }}>
        <PopoverTrigger asChild>
          <button 
            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-200 hover:bg-slate-300 text-slate-600 transition-colors"
            title="Zobrazit detail hodnotitelů"
          >
            <Info className="w-3 h-3" />
          </button>
        </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
          </div>
        ) : teamStatus ? (
          <div className="space-y-3">
            {teamStatus.evaluated_team?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-green-700 mb-1.5">✓ Ohodnotili:</p>
                <div className="flex flex-wrap gap-1">
                  {teamStatus.evaluated_team.map((member) => (
                    <span
                      key={member.id}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700"
                    >
                      {member.first_name} {member.last_name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {teamStatus.pending_evaluators?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-amber-700 mb-1.5">⏳ Čekají na hodnocení:</p>
                <div className="flex flex-wrap gap-1">
                  {teamStatus.pending_evaluators.map((member) => (
                    <span
                      key={member.id}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700"
                    >
                      {member.first_name} {member.last_name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {teamStatus.scheduled_team?.length === 0 && (
              <p className="text-xs text-slate-500">Žádný tým naplánován</p>
            )}
          </div>
        ) : (
          <p className="text-xs text-slate-500">Nepodařilo se načíst</p>
        )}
      </PopoverContent>
    </Popover>
    </div>
  );
};

export const SensorikaPage = ({ sensoryType = 'hotovky' }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL params
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');
  const [dateFrom, setDateFrom] = useState(searchParams.get('dateFrom') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('dateTo') || '');
  const [timePeriod, setTimePeriod] = useState(searchParams.get('period') || '1'); // Default: today

  // Quick evaluation states
  const [teamMembers, setTeamMembers] = useState([]);
  const [allEvalFields, setAllEvalFields] = useState([]);
  const [memberSelectDialogOpen, setMemberSelectDialogOpen] = useState(false);
  const [evalDialogOpen, setEvalDialogOpen] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [ratings, setRatings] = useState({});
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [pagination, setPagination] = useState({ total: 0, pages: 0 });
  const LIMIT = 50;

  const typeLabel = sensoryType === 'hotovky' ? 'Hotovky' : 'Krabičky';
  const basePath = `/senzorika/${sensoryType}`;

  // Update URL when filters change
  const updateUrlParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value && value !== '' && value !== '1' && key !== 'page') {
        newParams.set(key, value);
      } else if (key === 'page' && value > 1) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams, { replace: true });
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      updateUrlParams({ search: searchTerm, page: 1 });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch when debounced search changes
  useEffect(() => {
    fetchTests(timePeriod, 1, debouncedSearch, dateFrom, dateTo);
  }, [debouncedSearch]);

  // Refetch when sensoryType changes - but preserve filters from URL
  useEffect(() => {
    setTests([]);
    const urlPage = parseInt(searchParams.get('page')) || 1;
    const urlSearch = searchParams.get('search') || '';
    const urlPeriod = searchParams.get('period') || '1';
    const urlDateFrom = searchParams.get('dateFrom') || '';
    const urlDateTo = searchParams.get('dateTo') || '';
    
    setPage(urlPage);
    setSearchTerm(urlSearch);
    setDebouncedSearch(urlSearch);
    setTimePeriod(urlPeriod);
    setDateFrom(urlDateFrom);
    setDateTo(urlDateTo);
    
    fetchTests(urlPeriod, urlPage, urlSearch, urlDateFrom, urlDateTo);
    fetchTeamMembers();
    fetchEvalFields();
  }, [sensoryType]);

  const fetchTests = async (days = '1', pageNum = 1, search = '', fromDate = '', toDate = '') => {
    setLoading(true);
    try {
      const params = days === 'all' 
        ? { all_records: true, page: pageNum, limit: LIMIT, sensory_type: sensoryType } 
        : { days: parseInt(days), page: pageNum, limit: LIMIT, sensory_type: sensoryType };
      
      // Add search parameter if provided
      if (search && search.trim()) {
        params.search = search.trim();
      }
      
      // Add date range filters if provided - convert dd.MM.yyyy to yyyy-MM-dd for backend
      if (fromDate) {
        // Convert dd.MM.yyyy to yyyy-MM-dd
        const parts = fromDate.split('.');
        if (parts.length === 3) {
          params.date_from = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
      }
      if (toDate) {
        // Convert dd.MM.yyyy to yyyy-MM-dd
        const parts = toDate.split('.');
        if (parts.length === 3) {
          params.date_to = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
      }
      
      const response = await api.get('/sensory/tests', { params });
      setTests(response.data.tests || []);
      setPagination(response.data.pagination || { total: 0, pages: 0 });
      setPage(pageNum);
      setInitialLoading(false);
    } catch (error) {
      toast.error('Chyba při načítání dat');
      setInitialLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setTimePeriod(newPeriod);
    setPage(1);
    setSearchTerm(''); // Clear search when changing period
    setDebouncedSearch('');
    setDateFrom('');
    setDateTo('');
    updateUrlParams({ period: newPeriod, search: '', page: 1, dateFrom: '', dateTo: '' });
    fetchTests(newPeriod, 1, '', '', '');
  };

  const handleDateFromChange = (val) => {
    const newDateFrom = val || '';
    setDateFrom(newDateFrom);
    setPage(1);
    updateUrlParams({ dateFrom: newDateFrom, page: 1 });
    fetchTests(timePeriod, 1, debouncedSearch, newDateFrom, dateTo);
  };

  const handleDateToChange = (val) => {
    const newDateTo = val || '';
    setDateTo(newDateTo);
    setPage(1);
    updateUrlParams({ dateTo: newDateTo, page: 1 });
    fetchTests(timePeriod, 1, debouncedSearch, dateFrom, newDateTo);
  };

  const handlePageChange = (newPage) => {
    updateUrlParams({ page: newPage });
    fetchTests(timePeriod, newPage, debouncedSearch, dateFrom, dateTo);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    updateUrlParams({ search: '', page: 1 });
    fetchTests(timePeriod, 1, '', dateFrom, dateTo);
  };

  const fetchTeamMembers = async () => {
    try {
      const res = await api.get('/sensory-team', { params: { sensory_type: sensoryType } });
      setTeamMembers(res.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const fetchEvalFields = async () => {
    try {
      const res = await api.get('/sensory/eval-fields', { params: { sensory_type: sensoryType } });
      setAllEvalFields(res.data.filter(f => f.is_active));
    } catch (error) {
      console.error('Error fetching eval fields:', error);
    }
  };

  // Compute visible eval fields based on selected member's group (for Krabicky)
  const visibleEvalFields = React.useMemo(() => {
    if (sensoryType !== 'krabicky' || !selectedMember) {
      return allEvalFields;
    }
    // If member is Group A, show all fields; otherwise filter out Group A only fields
    const isGroupA = selectedMember.is_group_a || false;
    return isGroupA 
      ? allEvalFields 
      : allEvalFields.filter(f => !f.is_group_a_only);
  }, [allEvalFields, selectedMember, sensoryType]);

  const handleQuickEvaluation = async (test, e) => {
    e.stopPropagation();
    setSelectedMember(null);
    setRatings({});
    setNote('');
    
    // Fetch full test details including evaluations
    try {
      const res = await api.get(`/sensory/tests/${test.id}`);
      setSelectedTest(res.data);
    } catch (error) {
      console.error('Error fetching test details:', error);
      setSelectedTest(test); // Fallback to basic test data
    }
    
    setMemberSelectDialogOpen(true);
  };

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setMemberSelectDialogOpen(false);
    setEvalDialogOpen(true);
  };

  const handleSubmitEvaluation = async () => {
    // Validate all visible fields have ratings
    for (const field of visibleEvalFields) {
      if (!ratings[field.name]) {
        toast.error(`Vyplňte hodnocení pro "${field.name}"`);
        return;
      }
    }
    
    // If rating B or C, note should be filled
    const hasBorC = Object.values(ratings).includes('B') || Object.values(ratings).includes('C');
    if (hasBorC && !note.trim()) {
      toast.error('Při hodnocení B nebo C je nutné vyplnit poznámku');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/sensory/tests/${selectedTest.id}/evaluations`, { 
        ratings, 
        note,
        evaluator_id: selectedMember.id,
        evaluator_name: `${selectedMember.first_name} ${selectedMember.last_name}`
      });
      toast.success('Hodnocení přidáno');
      setEvalDialogOpen(false);
      setSelectedTest(null);
      setSelectedMember(null);
      setRatings({});
      setNote('');
      fetchTests(); // Refresh list
    } catch (error) {
      toast.error('Chyba při ukládání hodnocení');
    } finally {
      setSubmitting(false);
    }
  };

  const setAllRatingsA = () => {
    const allA = {};
    visibleEvalFields.forEach(field => { allA[field.name] = 'A'; });
    setRatings(allA);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
  };

  const setTodayFilter = () => {
    const today = format(new Date(), 'dd.MM.yyyy');
    setDateFrom(today);
    setDateTo(today);
  };

  // Helper to parse date from dd.MM.yyyy format
  const parseDateFilter = (dateStr) => {
    if (!dateStr) return null;
    try {
      const parts = dateStr.split('.');
      if (parts.length === 3) {
        return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
      }
    } catch {}
    return null;
  };

  // Filter by date range only (search is done on backend)
  const filteredTests = tests.filter(test => {
    // Date filter - filter by test_date (Den zkoušky)
    if (dateFrom || dateTo) {
      try {
        const testDate = parseISO(test.test_date);
        const fromDate = parseDateFilter(dateFrom);
        const toDate = parseDateFilter(dateTo);
        
        if (fromDate && toDate) {
          const from = startOfDay(fromDate);
          const to = endOfDay(toDate);
          if (!isWithinInterval(testDate, { start: from, end: to })) return false;
        } else if (fromDate) {
          const from = startOfDay(fromDate);
          if (testDate < from) return false;
        } else if (toDate) {
          const to = endOfDay(toDate);
          if (testDate > to) return false;
        }
      } catch {
        return true;
      }
    }
    
    return true;
  });

  const hasActiveFilters = searchTerm || dateFrom || dateTo;

  // Show full loading screen only on initial load, not during search/pagination
  const [initialLoading, setInitialLoading] = useState(true);

  if (initialLoading && loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-500 font-medium">Načítání...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 py-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Senzorika - {typeLabel}</h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">Senzorické zkoušky a hodnocení</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button 
              variant="outline"
              onClick={() => navigate(`${basePath}/tym`)}
              className="border-purple-200 text-purple-700 hover:bg-purple-50"
            >
              <Users className="h-4 w-4 mr-2" />
              Rozpis týmu
            </Button>
            <Button 
              onClick={() => navigate(`${basePath}/new`)}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 w-full sm:w-auto"
              data-testid="new-sensory-test-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nová zkouška
            </Button>
          </div>
        </div>

        {/* Time Period Quick Filters */}
        <div className="flex gap-2 flex-wrap">
          {[
            { value: '1', label: 'Dnes' },
            { value: '7', label: 'Posledních 7 dní' },
            { value: '30', label: 'Posledních 30 dní' },
            { value: '90', label: 'Posledních 90 dní' },
            { value: 'all', label: 'Všechny záznamy' },
          ].map((period) => (
            <Button
              key={period.value}
              variant={timePeriod === period.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => handlePeriodChange(period.value)}
              className={timePeriod === period.value 
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0' 
                : 'border-purple-200 text-purple-700 hover:bg-purple-50'
              }
              data-testid={`period-${period.value}`}
            >
              {period.label}
            </Button>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            {/* Search */}
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="text-sm font-medium text-slate-600 mb-1 block">Hledat (ve všech záznamech)</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={sensoryType === 'krabicky' ? "Název, šarže nebo program..." : "Název nebo šarže..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-8"
                  data-testid="sensory-search"
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              {searchTerm && (
                <p className="text-xs text-purple-600 mt-1">Hledání ignoruje časový filtr</p>
              )}
            </div>
            
            {/* Date From */}
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1 block">Datum od</label>
              <DatePicker
                value={dateFrom}
                onChange={handleDateFromChange}
                placeholder="Od..."
              />
            </div>
            
            {/* Date To */}
            <div>
              <label className="text-sm font-medium text-slate-600 mb-1 block">Datum do</label>
              <DatePicker
                value={dateTo}
                onChange={handleDateToChange}
                placeholder="Do..."
              />
            </div>
            
            {/* Buttons row */}
            <div className="flex gap-2 sm:col-span-2 lg:col-span-1">
              {/* Today filter */}
              <Button 
                variant="outline" 
                onClick={setTodayFilter}
                className={`flex-1 sm:flex-none ${dateFrom === format(new Date(), 'yyyy-MM-dd') && dateTo === format(new Date(), 'yyyy-MM-dd') ? 'bg-purple-100 border-purple-300 text-purple-700' : ''}`}
              >
                <Calendar className="h-4 w-4 mr-1" />
                Dnes
              </Button>
              
              {/* Clear filters */}
              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  onClick={clearFilters}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="h-4 w-4 mr-1" />
                  Zrušit
                </Button>
              )}
            </div>
          </div>
          
          {/* Active filters info */}
          {hasActiveFilters && (
            <div className="mt-3 pt-3 border-t border-slate-100 text-sm text-slate-500">
              Zobrazeno {filteredTests.length} z {tests.length} zkoušek
            </div>
          )}
        </div>

        {/* Tests List */}
        {loading && !initialLoading ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
            <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-slate-500 text-sm">Načítání...</p>
          </div>
        ) : filteredTests.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 sm:p-12 text-center">
            <FlaskConical className="h-12 sm:h-16 w-12 sm:w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg sm:text-xl font-semibold text-slate-700 mb-2">
              {searchTerm || dateFrom || dateTo ? 'Žádné výsledky' : 'Zatím žádné zkoušky'}
            </h3>
            <p className="text-slate-500 mb-6 text-sm sm:text-base">
              {searchTerm || dateFrom || dateTo ? 'Zkuste upravit filtry' : 'Vytvořte první senzorickou zkoušku'}
            </p>
            {!searchTerm && !dateFrom && !dateTo && (
              <Button 
                onClick={() => navigate(`${basePath}/new`)}
                className="bg-gradient-to-r from-purple-500 to-pink-500"
              >
                <Plus className="h-4 w-4 mr-2" />
                Vytvořit zkoušku
              </Button>
            )}
          </div>
        ) : (
          <>
          <div className="grid gap-4">
            {filteredTests.map((test) => {
              const colorClass = getRowColorClass(test);
              // Use pre-computed has_attachments from backend (optimization)
              const hasAttachments = test.has_attachments || test.images?.length > 0 || test.attachments?.length > 0;
              
              return (
                <div
                  key={test.id}
                  onClick={() => navigate(`${basePath}/${test.id}`)}
                  className={`bg-white rounded-xl border p-4 sm:p-5 hover:shadow-md transition-all cursor-pointer group ${colorClass || 'border-slate-200 hover:border-purple-300'}`}
                  data-testid={`sensory-test-${test.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                        test.rating_summary?.C > 0 
                          ? 'bg-gradient-to-br from-red-100 to-red-200 group-hover:from-red-200 group-hover:to-red-300' 
                          : test.rating_summary?.B > 0 
                            ? 'bg-gradient-to-br from-amber-100 to-amber-200 group-hover:from-amber-200 group-hover:to-amber-300'
                            : test.rating_summary?.A > 0
                              ? 'bg-gradient-to-br from-green-100 to-green-200 group-hover:from-green-200 group-hover:to-green-300'
                              : 'bg-gradient-to-br from-purple-100 to-pink-100 group-hover:from-purple-200 group-hover:to-pink-200'
                      }`}>
                        <FlaskConical className={`h-5 w-5 sm:h-6 sm:w-6 ${
                          test.rating_summary?.C > 0 
                            ? 'text-red-600' 
                            : test.rating_summary?.B > 0 
                              ? 'text-amber-600'
                              : test.rating_summary?.A > 0
                                ? 'text-green-600'
                                : 'text-purple-600'
                        }`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900 text-base sm:text-lg group-hover:text-purple-700 transition-colors truncate">
                            {test.food_name}
                          </h3>
                          {/* Show program for Krabicky */}
                          {sensoryType === 'krabicky' && test.program && (
                            <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs">
                              {test.program}
                            </Badge>
                          )}
                          {hasAttachments && (
                            <Paperclip className="h-4 w-4 text-slate-400 flex-shrink-0" title="Obsahuje přílohy" />
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 sm:mt-2 text-xs sm:text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Package className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          {test.batch}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          {formatDate(test.test_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                          {test.evaluation_count || 0} hodnocení
                        </span>
                      </div>
                      {/* Rating summary */}
                      {test.evaluation_count > 0 && test.rating_summary && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                            A: {test.rating_summary.A || 0}
                          </span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                            B: {test.rating_summary.B || 0}
                          </span>
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                            C: {test.rating_summary.C || 0}
                          </span>
                        </div>
                      )}
                      {/* Evaluators status - optimized: show only counts with info popover */}
                      {test.scheduled_count > 0 && (
                        <div className="flex items-center gap-2 mt-2">
                          <Users className="w-4 h-4 text-slate-400" />
                          <span className={`text-sm font-medium ${
                            test.evaluated_count >= test.scheduled_count 
                              ? 'text-green-600' 
                              : test.evaluated_count > 0 
                                ? 'text-amber-600' 
                                : 'text-slate-500'
                          }`}>
                            {test.evaluated_count}/{test.scheduled_count}
                          </span>
                          <span className="text-xs text-slate-400">hodnotilo</span>
                          <TeamStatusPopover testId={test.id} />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-start sm:self-center">
                    {/* Quick evaluation button */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-purple-200 text-purple-700 hover:bg-purple-50 hover:border-purple-300"
                      onClick={(e) => handleQuickEvaluation(test, e)}
                      title="Rychlé hodnocení"
                    >
                      <ClipboardPlus className="h-4 w-4 sm:mr-1" />
                      <span className="hidden sm:inline">Hodnotit</span>
                    </Button>
                    <Badge 
                      variant="secondary" 
                      className={`text-xs whitespace-nowrap ${
                        test.evaluation_count > 0 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {test.evaluation_count > 0 ? 'Hodnoceno' : 'Čeká'}
                    </Badge>
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="border-purple-200"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600 px-4">
                Stránka {page} z {pagination.pages} ({pagination.total} záznamů)
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === pagination.pages}
                className="border-purple-200"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
        )}
      </div>

      {/* Member selection dialog */}
      <Dialog open={memberSelectDialogOpen} onOpenChange={setMemberSelectDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Vyberte hodnotitele</DialogTitle>
            <DialogDescription>
              {selectedTest && (
                <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-purple-50 rounded-lg border border-purple-100">
                  <FlaskConical className="h-4 w-4 text-purple-500" />
                  <span className="font-medium text-purple-800">{selectedTest.food_name}</span>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-4 max-h-[300px] overflow-y-auto">
            {teamMembers.map((member) => {
              const hasEvaluated = selectedTest?.evaluations?.some(e => e.evaluator_id === member.id);
              return (
                <button
                  key={member.id}
                  onClick={() => handleSelectMember(member)}
                  disabled={hasEvaluated}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                    hasEvaluated 
                      ? 'bg-slate-50 border-slate-200 opacity-50 cursor-not-allowed' 
                      : 'border-slate-200 hover:border-purple-300 hover:bg-purple-50'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                    hasEvaluated ? 'bg-green-500' : 'bg-gradient-to-br from-purple-500 to-pink-500'
                  }`}>
                    {hasEvaluated ? <Check className="h-5 w-5" /> : `${member.first_name?.[0]}${member.last_name?.[0]}`}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{member.first_name} {member.last_name}</p>
                    <p className="text-sm text-slate-500">{member.email}</p>
                  </div>
                  {hasEvaluated && (
                    <span className="ml-auto text-xs text-green-600 font-medium">Již hodnotil/a</span>
                  )}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Evaluation form dialog */}
      <Dialog open={evalDialogOpen} onOpenChange={(open) => {
        setEvalDialogOpen(open);
        if (!open) {
          setSelectedMember(null);
          setRatings({});
          setNote('');
        }
      }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nové hodnocení</DialogTitle>
            <DialogDescription>
              <div className="space-y-2 mt-2">
                {/* Food name */}
                {selectedTest && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg border border-purple-100">
                    <FlaskConical className="h-4 w-4 text-purple-500" />
                    <span className="font-medium text-purple-800">{selectedTest.food_name}</span>
                    {selectedTest.batch && (
                      <span className="text-purple-600 text-sm">• Šarže: {selectedTest.batch}</span>
                    )}
                  </div>
                )}
                {/* Evaluator */}
                {selectedMember && (
                  <span className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white text-xs font-semibold">
                      {selectedMember.first_name[0]}{selectedMember.last_name[0]}
                    </span>
                    <span className="font-medium text-slate-700">
                      {selectedMember.first_name} {selectedMember.last_name}
                    </span>
                  </span>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Rating explanation */}
            <div className="bg-slate-50 rounded-lg p-3 text-sm">
              <p className="font-medium text-slate-700 mb-1">Stupnice hodnocení:</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-slate-600">
                <span><span className="font-semibold text-green-600">A</span> - Zcela vyhovující</span>
                <span><span className="font-semibold text-amber-600">B</span> - S výhradami</span>
                <span><span className="font-semibold text-red-600">C</span> - Nevyhovující</span>
              </div>
            </div>

            {/* Quick fill button - all A */}
            <Button
              type="button"
              variant="outline"
              className="w-full border-green-300 text-green-700 hover:bg-green-50 hover:border-green-400"
              onClick={setAllRatingsA}
            >
              <Check className="h-4 w-4 mr-2" />
              Zcela vyhovující (všechny A)
            </Button>
            
            {/* Rating fields - compact grid */}
            <div className="grid gap-3">
              {visibleEvalFields.map((field) => (
                <div key={field.id} className="flex items-center justify-between gap-4 p-2 bg-slate-50 rounded-lg">
                  <Label className="font-medium text-slate-700 min-w-[100px]">{field.name}</Label>
                  <div className="flex gap-1">
                    {['A', 'B', 'C'].map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setRatings(prev => ({ ...prev, [field.name]: value }))}
                        className={`w-10 h-10 rounded-lg font-bold transition-all ${
                          ratings[field.name] === value
                            ? value === 'A' 
                              ? 'bg-green-500 text-white shadow-lg scale-110' 
                              : value === 'B' 
                                ? 'bg-amber-500 text-white shadow-lg scale-110' 
                                : 'bg-red-500 text-white shadow-lg scale-110'
                            : 'bg-white border border-slate-200 text-slate-400 hover:border-slate-300'
                        }`}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Note */}
            <div className="space-y-2">
              <Label>
                Poznámka
                {(Object.values(ratings).includes('B') || Object.values(ratings).includes('C')) && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Doplňující poznámky k hodnocení..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEvalDialogOpen(false)}>
              Zrušit
            </Button>
            <Button 
              onClick={handleSubmitEvaluation}
              disabled={submitting}
              className="bg-gradient-to-r from-purple-500 to-pink-500"
            >
              {submitting ? 'Ukládám...' : 'Uložit hodnocení'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default SensorikaPage;
